import React, { useState, useEffect, useRef, useContext } from 'react'
import { Formik, Form, Field, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import axios from 'axios'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faClipboardList, faSave, faSearch, faUser, faTimes, faRightLeft } from '@fortawesome/free-solid-svg-icons'
import Swal from 'sweetalert2'
import { useParams } from 'react-router-dom'
import CustomLoader from '../layouts/CustomLoader'
import DatePicker from 'react-datepicker'
import { DateTime } from 'luxon'
import AuthContext from '../../middlewares/AuthContext'

const url = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_URL
  : process.env.REACT_APP_URL_DEV

const BRAND      = '#6eb446'
const BRAND_DARK = '#2d5918'

interface AuthCtx {
  getHeaders: () => Promise<Record<string, string>>
}

interface StatusOption {
  id   : number
  name : string
}

interface LicensePackage {
  serial_number: string
}

interface LicenseUser {
  id    : number
  email : string
}

interface License {
  StatusId        : number
  expireAt        : string
  product_key     : string
  Package         : LicensePackage
  User            : LicenseUser | null
  is_multiplatform: boolean
}

interface ApiResponse {
  message  : License
  statuses : StatusOption[]
  response?: string
}

interface UserResult {
  id    : number
  email : string
}

const statusColor: Record<number, string> = {
  1: '#6eb446',
  2: '#ef4444',
  3: '#3b82f6',
  4: '#3b82f6',
}

const formSchema = Yup.object().shape({
  StatusId: Yup.number().min(1).max(7).required(),
})

const EditLicense: React.FC = () => {
  const { getHeaders }            = useContext(AuthContext) as AuthCtx
  const { t }                     = useTranslation()
  const { id }                    = useParams<{ id: string }>()

  const [license, setLicense]     = useState<License | null>(null)
  const [statuses, setStatuses]   = useState<StatusOption[]>([])
  const [loaded, setLoaded]       = useState(false)
  const [loading, setLoading]     = useState(false)
  const [expireDate, setExpireDate] = useState<Date | null>(null)
  const [dateError, setDateError] = useState(false)

  // transfer owner state
  const [userQuery, setUserQuery]         = useState('')
  const [userResults, setUserResults]     = useState<UserResult[]>([])
  const [selectedUser, setSelectedUser]   = useState<UserResult | null>(null)
  const [searching, setSearching]         = useState(false)
  const [transferring, setTransferring]   = useState(false)
  const searchRef                         = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const fetch = async () => {
      try {
        const headers = await getHeaders()
        const res     = await axios.get<ApiResponse>(`${url}licenses/edit/${id}`, { headers })
        setLicense(res.data.message)
        setStatuses(res.data.statuses)
        setExpireDate(new Date(res.data.message.expireAt))
      } catch (err: any) {
        Swal.fire({ icon: 'error', title: err?.response?.data?.response || 'Error' })
      } finally {
        setLoaded(true)
      }
    }
    fetch()
  }, [])

  // debounced user search
  useEffect(() => {
    if (userQuery.trim().length < 2) {
      setUserResults([])
      return
    }
    const timer = setTimeout(async () => {
      setSearching(true)
      try {
        const headers = await getHeaders()
        const res     = await axios.get<{ message: UserResult[] }>(
          `${url}users/search?email=${encodeURIComponent(userQuery)}`,
          { headers }
        )
        setUserResults(res.data.message)
      } catch {
        setUserResults([])
      } finally {
        setSearching(false)
      }
    }, 350)
    return () => clearTimeout(timer)
  }, [userQuery])

  // close results when clicking outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setUserResults([])
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const handleSubmit = async (values: { StatusId: number }) => {
    if (!expireDate) { setDateError(true); return }
    const dt = DateTime.fromJSDate(expireDate)
    if (!dt.isValid) { setDateError(true); return }
    setDateError(false)

    const expireAtChanged = license && dt.toFormat('yyyy-MM-dd') !== DateTime.fromISO(license.expireAt).toFormat('yyyy-MM-dd')

    if (license?.is_multiplatform && expireAtChanged) {
      const confirm = await Swal.fire({
        icon              : 'warning',
        title             : '¡PELIGRO! Suscripción multiplataforma',
        html              : `Esta suscripción está vinculada a <b>Codaly</b>.<br/>Si cambias la fecha de expiración, <b>también cambiará en Codaly</b> para mantener ambas plataformas sincronizadas.<br/><br/>¿Deseas continuar?`,
        showCancelButton  : true,
        confirmButtonText : 'Sí, cambiar en ambas plataformas',
        cancelButtonText  : 'Cancelar',
        confirmButtonColor: '#dc3545',
        cancelButtonColor : '#6c757d',
        reverseButtons    : true,
      })
      if (!confirm.isConfirmed) return
    }

    setLoading(true)
    try {
      const headers = await getHeaders()
      const res     = await axios.put<{ response: string; message: License; codalySync?: { synced: boolean; error?: string } | null }>(
        `${url}licenses/update/${id}`,
        { ...values, expireAt: dt.toFormat('yyyy-MM-dd') },
        { headers }
      )
      setLicense(res.data.message)
      if (res.data.codalySync && !res.data.codalySync.synced) {
        Swal.fire({
          icon             : 'warning',
          title            : 'Actualizado en Portal, pero no se pudo sincronizar con Codaly',
          text             : res.data.codalySync.error || 'Codaly no respondió. Vuelve a intentarlo o notifica al equipo.',
          confirmButtonColor: '#dc3545',
        })
      } else {
        Swal.fire({ icon: 'success', title: res.data.response })
      }
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: err?.response?.data?.response || 'Error' })
    } finally {
      setLoading(false)
    }
  }

  const handleTransfer = async () => {
    if (!selectedUser) return
    const confirm = await Swal.fire({
      icon             : 'warning',
      title            : 'Transfer License?',
      html             : `Assign this license to <b>${selectedUser.email}</b>?`,
      showCancelButton : true,
      confirmButtonText: 'Yes, transfer',
      confirmButtonColor: BRAND,
    })
    if (!confirm.isConfirmed) return

    setTransferring(true)
    try {
      const headers = await getHeaders()
      const dt      = expireDate ? DateTime.fromJSDate(expireDate) : DateTime.fromISO(license!.expireAt)
      const res     = await axios.put<{ response: string; message: License }>(
        `${url}licenses/update/${id}`,
        {
          StatusId: license!.StatusId,
          expireAt: dt.toFormat('yyyy-MM-dd'),
          UserId  : selectedUser.id,
        },
        { headers }
      )
      setLicense(res.data.message)
      setSelectedUser(null)
      setUserQuery('')
      Swal.fire({ icon: 'success', title: res.data.response || 'License transferred' })
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: err?.response?.data?.response || 'Error' })
    } finally {
      setTransferring(false)
    }
  }

  if (!loaded) return <CustomLoader />

  if (!license) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 300 }}>
        <p className="text-muted">License not found.</p>
      </div>
    )
  }

  const currentStatus = statuses.find(s => s.id === license.StatusId)
  const badgeColor    = statusColor[license.StatusId] ?? '#94a3b8'

  return (
    <div className="row justify-content-center">
      <div className="col-md-8 col-lg-6">

        {/* ── Header card ──────────────────────────────────────────── */}
        <div style={{
          backgroundColor : '#fff',
          borderRadius    : 16,
          boxShadow       : '0 4px 20px rgba(0,0,0,0.07)',
          border          : '1px solid #f1f5f9',
          overflow        : 'hidden',
          marginBottom    : '1.5rem',
        }}>
          <div style={{ height: 4, background: `linear-gradient(90deg, ${BRAND_DARK}, ${BRAND})` }} />
          <div style={{ padding: '1.25rem 1.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{
              width           : 40,
              height          : 40,
              borderRadius    : 10,
              backgroundColor : `${BRAND}18`,
              display         : 'flex',
              alignItems      : 'center',
              justifyContent  : 'center',
              flexShrink      : 0,
            }}>
              <FontAwesomeIcon icon={faClipboardList} style={{ color: BRAND, fontSize: '1.1rem' }} />
            </div>
            <div>
              <p style={{ margin: 0, fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                {t('packages.show.edit')}
              </p>
              <p style={{ margin: 0, fontSize: '1rem', fontWeight: 800, color: '#1e293b' }}>
                {license.product_key}
              </p>
            </div>
          </div>
        </div>

        {/* ── Info strip ───────────────────────────────────────────── */}
        <div style={{
          backgroundColor : '#f8fafc',
          borderRadius    : 12,
          border          : '1px solid #e2e8f0',
          padding         : '1rem 1.25rem',
          marginBottom    : '1.25rem',
          display         : 'flex',
          flexWrap        : 'wrap',
          gap             : '1rem',
        }}>
          <div>
            <p style={{ margin: 0, fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>
              {t('licenses.product_key')}
            </p>
            <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: 700, color: '#1e293b', fontFamily: 'monospace' }}>
              {license.product_key}
            </p>
          </div>
          <div>
            <p style={{ margin: 0, fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>
              {t('licenses.package')}
            </p>
            <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: 700, color: '#1e293b' }}>
              {license.Package.serial_number}
            </p>
          </div>
          <div>
            <p style={{ margin: 0, fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>
              {t('licenses.customer.status')}
            </p>
            <span style={{
              display         : 'inline-block',
              padding         : '2px 10px',
              borderRadius    : 9999,
              backgroundColor : `${badgeColor}18`,
              color           : badgeColor,
              fontWeight      : 700,
              fontSize        : '0.78rem',
              textTransform   : 'capitalize',
              border          : `1px solid ${badgeColor}40`,
            }}>
              {currentStatus?.name ?? '—'}
            </span>
          </div>
          <div>
            <p style={{ margin: 0, fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>
              Owner
            </p>
            <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: 600, color: license.User ? '#1e293b' : '#94a3b8' }}>
              {license.User?.email ?? '—'}
            </p>
          </div>
        </div>

        {/* ── Multiplatform danger banner ──────────────────────────── */}
        {license.is_multiplatform && (
          <div style={{
            backgroundColor : '#fef2f2',
            border          : '2px solid #dc3545',
            borderRadius    : 12,
            padding         : '1rem 1.25rem',
            marginBottom    : '1.25rem',
            display         : 'flex',
            alignItems      : 'flex-start',
            gap             : '0.75rem',
            boxShadow       : '0 4px 14px rgba(220,53,69,0.25)',
          }}>
            <FontAwesomeIcon icon={faClipboardList} style={{ color: '#dc3545', fontSize: '1.4rem', marginTop: 2 }} />
            <div>
              <p style={{ margin: 0, fontSize: '0.85rem', fontWeight: 800, color: '#dc3545', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                ¡Peligro! Suscripción multiplataforma (Codaly)
              </p>
              <p style={{ margin: '0.3rem 0 0', fontSize: '0.85rem', fontWeight: 600, color: '#7f1d1d' }}>
                Este código también está activo en <b>Codaly</b>. Si cambias la fecha de expiración aquí,
                también cambiará allá para mantener ambas plataformas sincronizadas.
              </p>
            </div>
          </div>
        )}

        {/* ── Form card ────────────────────────────────────────────── */}
        <div style={{
          backgroundColor : '#fff',
          borderRadius    : 16,
          boxShadow       : '0 4px 20px rgba(0,0,0,0.07)',
          border          : '1px solid #f1f5f9',
          overflow        : 'hidden',
          marginBottom    : '1.25rem',
        }}>
          <div style={{ height: 3, backgroundColor: BRAND }} />
          <div style={{ padding: '1.5rem' }}>
            <Formik
              initialValues={{ StatusId: license.StatusId }}
              validationSchema={formSchema}
              onSubmit={handleSubmit}
            >
              <Form>
                {/* Expire at */}
                <div className="mb-4">
                  <label
                    htmlFor="expireAt"
                    style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.8rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}
                  >
                    {t('licenses.customer.expire_at')}
                  </label>
                  <DatePicker
                    id="expireAt"
                    dateFormat="yyyy-MM-dd"
                    selected={expireDate}
                    onChange={(date) => { setExpireDate(date); setDateError(false) }}
                    className="form-control form-control-sm"
                    placeholderText="yyyy-MM-dd"
                  />
                  {dateError && (
                    <p style={{ margin: '0.35rem 0 0', fontSize: '0.78rem', color: '#ef4444', fontWeight: 600 }}>
                      Invalid date
                    </p>
                  )}
                </div>

                {/* Status */}
                <div className="mb-4">
                  <label
                    htmlFor="StatusId"
                    style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.8rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}
                  >
                    {t('packages.status_license')}
                  </label>
                  <Field
                    as="select"
                    id="StatusId"
                    name="StatusId"
                    className="form-select form-select-sm text-capitalize"
                  >
                    {statuses.map((s) => (
                      <option key={s.id} value={s.id} className="text-capitalize">
                        {s.name}
                      </option>
                    ))}
                  </Field>
                  <ErrorMessage name="StatusId" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    display         : 'inline-flex',
                    alignItems      : 'center',
                    gap             : '0.5rem',
                    padding         : '0.55rem 1.5rem',
                    borderRadius    : 9999,
                    backgroundColor : loading ? '#94a3b8' : BRAND,
                    color           : '#fff',
                    fontWeight      : 700,
                    fontSize        : '0.875rem',
                    border          : 'none',
                    cursor          : loading ? 'not-allowed' : 'pointer',
                    boxShadow       : loading ? 'none' : `0 4px 14px ${BRAND}55`,
                    transition      : 'filter 0.15s, transform 0.15s',
                    width           : '100%',
                    justifyContent  : 'center',
                  }}
                  onMouseEnter={e => { if (!loading) { (e.currentTarget as HTMLButtonElement).style.filter = 'brightness(1.09)'; (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(-1px)' }}}
                  onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.filter = 'brightness(1)'; (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(0)' }}
                >
                  {loading
                    ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                    : <FontAwesomeIcon icon={faSave} />
                  }
                  <span>{t('packages.show.edit')}</span>
                </button>
              </Form>
            </Formik>
          </div>
        </div>

        {/* ── Transfer owner card ───────────────────────────────────── */}
        <div style={{
          backgroundColor : '#fff',
          borderRadius    : 16,
          boxShadow       : '0 4px 20px rgba(0,0,0,0.07)',
          border          : '1px solid #f1f5f9',
          overflow        : 'hidden',
        }}>
          <div style={{ height: 3, background: 'linear-gradient(90deg, #f59e0b, #ef4444)' }} />
          <div style={{ padding: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
              <FontAwesomeIcon icon={faRightLeft} style={{ color: '#f59e0b', fontSize: '0.95rem' }} />
              <p style={{ margin: 0, fontSize: '0.8rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Transfer License Owner
              </p>
            </div>

            {/* Current owner */}
            <div style={{
              padding         : '0.6rem 0.9rem',
              borderRadius    : 8,
              backgroundColor : '#f8fafc',
              border          : '1px solid #e2e8f0',
              marginBottom    : '1rem',
              fontSize        : '0.82rem',
              color           : '#64748b',
            }}>
              <FontAwesomeIcon icon={faUser} style={{ marginRight: '0.4rem', color: '#94a3b8' }} />
              <span style={{ fontWeight: 600 }}>Current owner: </span>
              <span style={{ fontFamily: 'monospace' }}>{license.User?.email ?? 'Unassigned'}</span>
            </div>

            {/* Search input */}
            <div ref={searchRef} style={{ position: 'relative', marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.8rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Search new owner
              </label>
              <div style={{ position: 'relative' }}>
                <FontAwesomeIcon
                  icon={faSearch}
                  style={{ position: 'absolute', left: '0.7rem', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: '0.8rem', pointerEvents: 'none' }}
                />
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Search by email..."
                  value={userQuery}
                  onChange={e => { setUserQuery(e.target.value); setSelectedUser(null) }}
                  style={{ paddingLeft: '2rem' }}
                />
                {searching && (
                  <span
                    className="spinner-border spinner-border-sm"
                    style={{ position: 'absolute', right: '0.7rem', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}
                  />
                )}
              </div>

              {/* Results dropdown */}
              {userResults.length > 0 && (
                <div style={{
                  position        : 'absolute',
                  top             : '100%',
                  left            : 0,
                  right           : 0,
                  zIndex          : 1000,
                  backgroundColor : '#fff',
                  border          : '1px solid #e2e8f0',
                  borderRadius    : 8,
                  boxShadow       : '0 8px 24px rgba(0,0,0,0.1)',
                  marginTop       : '0.25rem',
                  overflow        : 'hidden',
                }}>
                  {userResults.map((u) => (
                    <div
                      key={u.id}
                      onClick={() => { setSelectedUser(u); setUserQuery(u.email); setUserResults([]) }}
                      style={{
                        padding   : '0.55rem 0.9rem',
                        cursor    : 'pointer',
                        fontSize  : '0.85rem',
                        color     : '#1e293b',
                        display   : 'flex',
                        alignItems: 'center',
                        gap       : '0.5rem',
                        transition: 'background 0.1s',
                      }}
                      onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#f0fdf4')}
                      onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                    >
                      <FontAwesomeIcon icon={faUser} style={{ color: BRAND, fontSize: '0.75rem', flexShrink: 0 }} />
                      <span style={{ fontFamily: 'monospace' }}>{u.email}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Selected user badge */}
            {selectedUser && (
              <div style={{
                display         : 'inline-flex',
                alignItems      : 'center',
                gap             : '0.4rem',
                padding         : '0.3rem 0.75rem',
                borderRadius    : 9999,
                backgroundColor : `${BRAND}15`,
                border          : `1px solid ${BRAND}40`,
                color           : BRAND_DARK,
                fontWeight      : 700,
                fontSize        : '0.8rem',
                marginBottom    : '1rem',
                fontFamily      : 'monospace',
              }}>
                <FontAwesomeIcon icon={faUser} style={{ fontSize: '0.75rem' }} />
                {selectedUser.email}
                <button
                  type="button"
                  onClick={() => { setSelectedUser(null); setUserQuery('') }}
                  style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#94a3b8', lineHeight: 1 }}
                >
                  <FontAwesomeIcon icon={faTimes} style={{ fontSize: '0.7rem' }} />
                </button>
              </div>
            )}

            {/* Transfer button */}
            <button
              type="button"
              disabled={!selectedUser || transferring}
              onClick={handleTransfer}
              style={{
                display         : 'inline-flex',
                alignItems      : 'center',
                gap             : '0.5rem',
                padding         : '0.55rem 1.5rem',
                borderRadius    : 9999,
                backgroundColor : (!selectedUser || transferring) ? '#94a3b8' : '#f59e0b',
                color           : '#fff',
                fontWeight      : 700,
                fontSize        : '0.875rem',
                border          : 'none',
                cursor          : (!selectedUser || transferring) ? 'not-allowed' : 'pointer',
                boxShadow       : (!selectedUser || transferring) ? 'none' : '0 4px 14px rgba(245,158,11,0.4)',
                transition      : 'filter 0.15s, transform 0.15s',
                width           : '100%',
                justifyContent  : 'center',
              }}
              onMouseEnter={e => { if (selectedUser && !transferring) { (e.currentTarget as HTMLButtonElement).style.filter = 'brightness(1.09)'; (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(-1px)' }}}
              onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.filter = 'brightness(1)'; (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(0)' }}
            >
              {transferring
                ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                : <FontAwesomeIcon icon={faRightLeft} />
              }
              <span>Transfer License</span>
            </button>
          </div>
        </div>

      </div>
    </div>
  )
}

export default EditLicense
